import { onScopeDispose, ref } from "vue"

import type { AppError } from "@/shared/api"
import { normalizeError } from "@/shared/api"

import * as clientApi from "../api/clientApi"
import type { Client } from "./types"

export const MIN_QUERY_LENGTH = 2

export function useClientSearch() {
  const query = ref("")
  const results = ref<Client[]>([])
  const limited = ref(false)
  const loading = ref(false)
  const error = ref<AppError | null>(null)

  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let abortController: AbortController | null = null

  function cancelPending(): void {
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }
    if (abortController !== null) {
      abortController.abort()
      abortController = null
    }
  }

  function setQuery(q: string): void {
    cancelPending()
    error.value = null
    query.value = q
    loading.value = false

    const trimmed = q.trim()
    if ([...trimmed].length < MIN_QUERY_LENGTH) {
      results.value = []
      limited.value = false
      return
    }

    debounceTimer = setTimeout(async () => {
      debounceTimer = null
      const controller = new AbortController()
      abortController = controller
      loading.value = true

      try {
        const { clients, limited: isLimited } = await clientApi.listClients(trimmed, controller.signal)
        results.value = clients
        limited.value = isLimited
      } catch (e) {
        if (controller.signal.aborted) return
        error.value = normalizeError(e)
        results.value = []
        limited.value = false
      } finally {
        loading.value = false
        if (abortController === controller) {
          abortController = null
        }
      }
    }, 300)
  }

  onScopeDispose(() => {
    cancelPending()
  })

  return { query, results, limited, loading, error, setQuery }
}
